import React, { useState } from 'react';
import { UserCheck, AlertTriangle, TrendingUp, Calendar, ShieldAlert, CheckCircle, Info } from 'lucide-react';
import { Category, EventItem } from '../../core/types.ts';

interface OrganisersPerDayGraphProps {
  events: EventItem[];
  maxOrganisersThreshold: number;
  onEditEvent?: (event: EventItem) => void;
}

const CATEGORIES: Category[] = [
  'Culture',
  'Drinking',
  'Sports',
  'Social',
  'Academic',
  'Trip',
  'Art',
  'Other',
];

const CATEGORY_COLOR_VARS: Record<Category, string> = {
  Culture: 'var(--cat-culture)',
  Drinking: 'var(--cat-drinking)',
  Sports: 'var(--cat-sports)',
  Social: 'var(--cat-social)',
  Academic: 'var(--cat-academic)',
  Trip: 'var(--cat-trip)',
  Art: 'var(--cat-art)',
  Other: 'var(--cat-other)',
};

export interface DailyOrganiserData {
  dateStr: string;
  formattedDate: string;
  dayOfWeek: string;
  events: EventItem[];
  totalOrganisers: number;
  peakConcurrent: number;
  totalSpots: number;
  byCategory: Record<Category, number>;
  exceedsThreshold: boolean;
}

export function computeDailyOrganiserData(
  events: EventItem[],
  startDateStr: string = '2026-09-28',
  numDays: number = 15,
  maxThreshold: number = 10
): DailyOrganiserData[] {
  const dateSet = new Set<string>();
  const baseDate = new Date(`${startDateStr}T00:00:00`);

  for (let i = 0; i < numDays; i++) {
    const d = new Date(baseDate.getTime() + i * 86400000);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    dateSet.add(`${yyyy}-${mm}-${dd}`);
  }

  for (const ev of events) {
    if (ev.start) {
      const dStr = ev.start.split('T')[0];
      if (dStr) dateSet.add(dStr);
    }
  }

  const sortedDates = Array.from(dateSet).sort();

  return sortedDates.map(dateStr => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const dObj = new Date(year, month - 1, day);
    const dayOfWeek = dObj.toLocaleDateString('en-US', { weekday: 'short' });
    const formattedDate = dObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    const dayEvents = events.filter(e => e.start && e.start.startsWith(dateStr));
    const totalOrganisers = dayEvents.reduce((acc, e) => acc + (Number(e.organisers) || 0), 0);
    const totalSpots = dayEvents.reduce((acc, e) => acc + (Number(e.capacity) || 0), 0);

    const byCategory: Record<Category, number> = {
      Culture: 0,
      Drinking: 0,
      Sports: 0,
      Social: 0,
      Academic: 0,
      Trip: 0,
      Art: 0,
      Other: 0,
    };

    for (const ev of dayEvents) {
      const cat = ev.category || 'Other';
      byCategory[cat] = (byCategory[cat] || 0) + (Number(ev.organisers) || 0);
    }

    // Peak concurrent organisers logic
    interface SweepPoint {
      time: number;
      type: 'start' | 'end';
      orgs: number;
    }
    const points: SweepPoint[] = [];
    for (const ev of dayEvents) {
      const s = new Date(ev.start).getTime();
      const e = new Date(ev.end).getTime();
      if (!isNaN(s) && !isNaN(e) && s < e) {
        points.push({ time: s, type: 'start', orgs: Number(ev.organisers) || 0 });
        points.push({ time: e, type: 'end', orgs: Number(ev.organisers) || 0 });
      }
    }
    points.sort((a, b) => (a.time !== b.time ? a.time - b.time : a.type === 'end' ? -1 : 1));

    let currentConcurrent = 0;
    let peakConcurrent = 0;
    for (const pt of points) {
      if (pt.type === 'start') {
        currentConcurrent += pt.orgs;
        if (currentConcurrent > peakConcurrent) {
          peakConcurrent = currentConcurrent;
        }
      } else {
        currentConcurrent -= pt.orgs;
      }
    }

    const exceedsThreshold = peakConcurrent > maxThreshold;

    return {
      dateStr,
      formattedDate,
      dayOfWeek,
      events: dayEvents,
      totalOrganisers,
      peakConcurrent,
      totalSpots,
      byCategory,
      exceedsThreshold,
    };
  });
}

export const OrganisersPerDayGraph: React.FC<OrganisersPerDayGraphProps> = ({
  events,
  maxOrganisersThreshold,
  onEditEvent,
}) => {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [viewMetric, setViewMetric] = useState<'total' | 'peak'>('total');

  const dailyData = computeDailyOrganiserData(events, '2026-09-28', 15, maxOrganisersThreshold);

  // Aggregated Summary Stats
  const totalOrgsShiftDemand = dailyData.reduce((acc, d) => acc + d.totalOrganisers, 0);
  const maxPeakConcurrent = Math.max(...dailyData.map(d => d.peakConcurrent), 0);
  const busiestDay = dailyData.reduce((prev, curr) => (curr.totalOrganisers > prev.totalOrganisers ? curr : prev), dailyData[0]);
  const highDemandDaysCount = dailyData.filter(d => d.exceedsThreshold).length;
  const activeDaysCount = dailyData.filter(d => d.events.length > 0).length;
  const avgOrgsPerActiveDay = activeDaysCount > 0 ? (totalOrgsShiftDemand / activeDaysCount).toFixed(1) : '0';

  // Chart Y-axis scaling
  const maxChartVal = Math.max(
    ...dailyData.map(d => (viewMetric === 'total' ? d.totalOrganisers : d.peakConcurrent)),
    maxOrganisersThreshold + 2,
    12
  );

  const selectedDayData = dailyData.find(d => d.dateStr === selectedDate) || null;

  return (
    <div className="organisers-graph-container" style={{ marginTop: '1.5rem' }}>
      {/* Top Stat Cards Ribbon */}
      <div className="rule-config-grid" style={{ marginBottom: '1.5rem' }}>
        <div className="config-card" style={{ borderLeft: '4px solid var(--accent-primary)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>Total Organiser Demand</span>
            <UserCheck size={16} color="var(--accent-primary)" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '0.4rem' }}>
            <span style={{ fontSize: '1.6rem', fontWeight: 800 }}>{totalOrgsShiftDemand} org-shifts</span>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Avg {avgOrgsPerActiveDay}/day</span>
          </div>
        </div>

        <div className="config-card" style={{ borderLeft: '4px solid #c084fc' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>Peak Simultaneous Staffing</span>
            <TrendingUp size={16} color="#c084fc" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '0.4rem' }}>
            <span style={{ fontSize: '1.6rem', fontWeight: 800 }}>{maxPeakConcurrent} orgs peak</span>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Max threshold: {maxOrganisersThreshold}</span>
          </div>
        </div>

        <div className="config-card" style={{ borderLeft: '4px solid #38bdf8' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>Busiest Schedule Day</span>
            <Calendar size={16} color="#38bdf8" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '0.4rem' }}>
            <span style={{ fontSize: '1.1rem', fontWeight: 700 }}>
              {busiestDay ? `${busiestDay.dayOfWeek}, ${busiestDay.formattedDate}` : 'N/A'}
            </span>
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--accent-primary)' }}>
              {busiestDay?.totalOrganisers || 0} orgs ({busiestDay?.events.length || 0} evs)
            </span>
          </div>
        </div>

        <div className="config-card" style={{ borderLeft: `4px solid ${highDemandDaysCount > 0 ? '#f59e0b' : '#10b981'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>High Demand Warnings</span>
            {highDemandDaysCount > 0 ? <AlertTriangle size={16} color="#f59e0b" /> : <CheckCircle size={16} color="#10b981" />}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '0.4rem' }}>
            <span style={{ fontSize: '1.6rem', fontWeight: 800, color: highDemandDaysCount > 0 ? '#fde047' : '#34d399' }}>
              {highDemandDaysCount} {highDemandDaysCount === 1 ? 'day' : 'days'}
            </span>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>&gt;{maxOrganisersThreshold} peak orgs</span>
          </div>
        </div>
      </div>

      {/* Main Graph Card */}
      <div
        style={{
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-xl)',
          padding: '1.5rem',
          boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header Controls */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.25rem' }}>
          <div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <UserCheck size={20} color="var(--accent-primary)" />
              <span>Required Organisers per Day Graph</span>
            </h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0.2rem 0 0' }}>
              Daily required staff demand breakdown across O-Weeks (Sept 28 – Oct 12, 2026)
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div className="view-controls" style={{ margin: 0 }}>
              <button
                className={`view-btn ${viewMetric === 'total' ? 'active' : ''}`}
                onClick={() => setViewMetric('total')}
                style={{ padding: '0.35rem 0.85rem', fontSize: '0.75rem' }}
              >
                Stacked Category Total
              </button>
              <button
                className={`view-btn ${viewMetric === 'peak' ? 'active' : ''}`}
                onClick={() => setViewMetric('peak')}
                style={{ padding: '0.35rem 0.85rem', fontSize: '0.75rem' }}
              >
                Peak Concurrent Demand
              </button>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.85rem', marginBottom: '1.5rem', padding: '0.6rem 1rem', background: 'rgba(255,255,255,0.02)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', fontSize: '0.75rem' }}>
          <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>Categories:</span>
          {CATEGORIES.map(cat => (
            <span key={cat} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: 'var(--text-secondary)' }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: CATEGORY_COLOR_VARS[cat] }} />
              {cat}
            </span>
          ))}
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: '#ef4444', fontWeight: 600 }}>
            <span style={{ width: 14, height: 2, background: '#ef4444', borderStyle: 'dashed' }} />
            Max Staffing Threshold ({maxOrganisersThreshold} orgs)
          </span>
        </div>

        {/* Chart Viewport */}
        <div style={{ position: 'relative', minHeight: '260px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', paddingTop: '2.5rem', paddingBottom: '0.5rem' }}>
          {/* Threshold Line */}
          {maxOrganisersThreshold > 0 && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: `${(maxOrganisersThreshold / maxChartVal) * 180 + 35}px`,
                borderTop: '2px dashed rgba(239, 68, 68, 0.75)',
                zIndex: 3,
                pointerEvents: 'none',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '-18px',
                  background: 'rgba(239, 68, 68, 0.2)',
                  color: '#fca5a5',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  padding: '0.1rem 0.4rem',
                  borderRadius: '4px',
                }}
              >
                Limit: {maxOrganisersThreshold} orgs
              </span>
            </div>
          )}

          {/* Columns Container */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: '0.5rem',
              height: '180px',
              borderBottom: '1px solid var(--border)',
              paddingBottom: '2px',
              position: 'relative',
              zIndex: 2,
            }}
          >
            {dailyData.map(day => {
              const isSelected = selectedDate === day.dateStr;
              const val = viewMetric === 'total' ? day.totalOrganisers : day.peakConcurrent;
              const heightPct = Math.min((val / maxChartVal) * 100, 100);

              return (
                <div
                  key={day.dateStr}
                  onClick={() => setSelectedDate(isSelected ? null : day.dateStr)}
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    height: '100%',
                    cursor: 'pointer',
                    position: 'relative',
                  }}
                  title={`${day.dayOfWeek}, ${day.formattedDate}: ${day.totalOrganisers} orgs required total (${day.peakConcurrent} peak simultaneous)`}
                >
                  {/* Top Value Label */}
                  <div
                    style={{
                      fontSize: '0.725rem',
                      fontWeight: 700,
                      marginBottom: '4px',
                      color: day.exceedsThreshold ? '#fde047' : val > 0 ? 'var(--text-primary)' : 'var(--text-dim)',
                    }}
                  >
                    {val > 0 ? val : '0'}
                  </div>

                  {/* Column Bar Shell */}
                  <div
                    style={{
                      width: '100%',
                      maxWidth: '36px',
                      height: `${Math.max(heightPct, val > 0 ? 6 : 2)}%`,
                      borderRadius: '6px 6px 2px 2px',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column-reverse',
                      background: val === 0 ? 'rgba(255, 255, 255, 0.05)' : undefined,
                      border: isSelected
                        ? '2px solid var(--accent-primary)'
                        : day.exceedsThreshold
                        ? '1px solid rgba(245, 158, 11, 0.8)'
                        : '1px solid rgba(255, 255, 255, 0.1)',
                      boxShadow: isSelected
                        ? '0 0 12px var(--accent-primary)'
                        : day.exceedsThreshold
                        ? '0 0 10px rgba(245, 158, 11, 0.3)'
                        : undefined,
                      transition: 'transform 0.2s ease, filter 0.2s ease',
                      transform: isSelected ? 'scale(1.06)' : 'none',
                    }}
                  >
                    {viewMetric === 'total' ? (
                      // Stacked categories breakdown
                      CATEGORIES.map(cat => {
                        const orgCount = day.byCategory[cat] || 0;
                        if (orgCount === 0 || day.totalOrganisers === 0) return null;
                        const catPct = (orgCount / day.totalOrganisers) * 100;

                        return (
                          <div
                            key={cat}
                            style={{
                              height: `${catPct}%`,
                              backgroundColor: CATEGORY_COLOR_VARS[cat],
                              opacity: 0.9,
                              transition: 'height 0.3s ease',
                            }}
                            title={`${cat}: ${orgCount} orgs`}
                          />
                        );
                      })
                    ) : (
                      // Peak single metric bar
                      <div
                        style={{
                          height: '100%',
                          width: '100%',
                          background: day.exceedsThreshold
                            ? 'linear-gradient(180deg, #f59e0b 0%, #d97706 100%)'
                            : 'linear-gradient(180deg, #818cf8 0%, #4f46e5 100%)',
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* X-Axis Labels */}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', marginTop: '0.6rem' }}>
            {dailyData.map(day => {
              const isSelected = selectedDate === day.dateStr;
              return (
                <div
                  key={day.dateStr}
                  onClick={() => setSelectedDate(isSelected ? null : day.dateStr)}
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    cursor: 'pointer',
                    color: isSelected ? 'var(--text-primary)' : 'var(--text-muted)',
                    fontWeight: isSelected ? 700 : 500,
                  }}
                >
                  <div style={{ fontSize: '0.725rem', fontFamily: 'var(--font-display)' }}>{day.dayOfWeek}</div>
                  <div style={{ fontSize: '0.675rem', opacity: 0.8 }}>{day.formattedDate.split(', ')[1] || day.formattedDate.split(' ')[1]}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Selected Day Event Inspection Panel */}
      {selectedDayData && (
        <div
          style={{
            marginTop: '1.25rem',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-glow)',
            borderRadius: 'var(--radius-lg)',
            padding: '1.25rem 1.5rem',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <div>
              <h4 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
                {selectedDayData.dayOfWeek}, {selectedDayData.formattedDate} Staffing Breakdown
              </h4>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                Total Required: {selectedDayData.totalOrganisers} orgs | Peak Concurrent: {selectedDayData.peakConcurrent} orgs | {selectedDayData.events.length} events scheduled
              </span>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => setSelectedDate(null)}>
              Close Breakdown
            </button>
          </div>

          {selectedDayData.events.length === 0 ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', italic: 'true' }}>
              No events scheduled on this day.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Event Title</th>
                    <th>Category</th>
                    <th>Organisers Needed</th>
                    <th>Total Capacity</th>
                    <th>Ratio</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedDayData.events.map(ev => {
                    const startStr = ev.start ? ev.start.split('T')[1]?.substring(0, 5) : '';
                    const endStr = ev.end ? ev.end.split('T')[1]?.substring(0, 5) : '';
                    const ratio = ev.organisers > 0 ? (ev.capacity / ev.organisers).toFixed(1) : '∞';

                    return (
                      <tr key={ev.id}>
                        <td>
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                            {startStr} – {endStr}
                          </span>
                        </td>
                        <td>
                          <strong style={{ color: 'var(--text-primary)' }}>{ev.title}</strong>
                        </td>
                        <td>
                          <span
                            className="cat-pill active"
                            style={{
                              padding: '0.15rem 0.5rem',
                              fontSize: '0.75rem',
                              borderLeft: `3px solid ${CATEGORY_COLOR_VARS[ev.category] || 'var(--cat-other)'}`,
                            }}
                          >
                            {ev.category}
                          </span>
                        </td>
                        <td>
                          <strong style={{ color: 'var(--accent-primary)' }}>{ev.organisers} orgs</strong>
                        </td>
                        <td>{ev.capacity} seats</td>
                        <td>{ratio} spots/org</td>
                        <td>
                          {onEditEvent && (
                            <button
                              className="btn btn-secondary btn-sm"
                              style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem' }}
                              onClick={() => onEditEvent(ev)}
                            >
                              Edit
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
